import { useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material'
import { HelpOutline } from '@mui/icons-material'
import {
  ImportActionsModal,
  CustomTableCell,
  TableHead,
} from '../../components'
import {
  ImportActionsModalProvider,
  useImportActionsModal,
} from '../../contexts/ImportActionsModalContext'
import useImportPage from '../../hooks/useImportPage'
import { getComparator } from '../../utils/graph'
import { useSnackbar } from '../../hooks'
import axiosInstance from '../../services/axiosInstance'
import { useTranslation } from "react-i18next"

const Container = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  background-color: #f1f3f5;
`

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 24px 44px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: '100%';
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 8px;
  padding: 16px;
`

const rowsPerPage = 10

const UserImportPage = () => {
  const { successSnackbar, errorSnackbar } = useSnackbar()
  const [loading, setLoading] = useState(false)
  const [order, setOrder] = useState('desc')
  const [page, setPage] = useState(0)
  const { openModal } = useImportActionsModal()
  const { loading: loadingData, rows } = useImportPage({ type: 'user' })
  const { t } = useTranslation('common')

  const headState = [
    {
      id: 'date',
      numeric: false,
      disablePadding: false,
      label: t('import.header.date'),
      align: 'left',
    },
    {
      id: 'status',
      numeric: false,
      disablePadding: false,
      label: t('import.header.status'),
      align: 'left',
    },
    {
      id: 'createdBy',
      numeric: false,
      disablePadding: false,
      label: t('import.header.created_by'),
      align: 'left',
    },
    {
      id: 'importCreatedCount',
      numeric: true,
      disablePadding: false,
      label: t('import.header.created_count'),
      align: 'left',
    },
    {
      id: 'importUpdatedCount',
      numeric: true,
      disablePadding: false,
      label: t('import.header.updated_count'),
      align: 'left',
    },
    {
      id: 'importErrorCount',
      numeric: true,
      disablePadding: false,
      label: t('import.header.error_count'),
      align: 'left',
    },
  ]
  const [orderBy, setOrderBy] = useState(headState[0].id)

  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleChangePage = (_, newPage) => {
    setPage(newPage)
  }

  const emptyRows = () => {
    return page > 0 || rows.length < 10
      ? Math.max(0, (1 + page) * rowsPerPage - rows.length)
      : 0
  }

  const visibleRows = useMemo(
    () =>
      [...rows]
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, rows],
  )

  const handleButtonClick = () => {
    openModal()
  }

  const getFilenameFromDisposition = (disposition) => {
    if (!disposition) return 'download.csv';

    // Try UTF-8 filename* format
    const utf8Match = disposition.match(/filename\*\=UTF-8''(.+)/);
    if (utf8Match) {
      return decodeURIComponent(utf8Match[1]);
    }

    // Fallback: plain filename
    const asciiMatch = disposition.match(/filename="(.+?)"/);
    if (asciiMatch) {
      return asciiMatch[1];
    }

    return 'users_processados.csv';
  };

  const handleDownloadCsv = async (id) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`user-imports/${id}/download`, {
        responseType: 'blob',
        headers: {
          Accept: 'text/csv',
        },
      });

      const blob = new Blob([response.data], { type: 'text/csv' });

      const disposition = response.headers['content-disposition'];
      const filename = getFilenameFromDisposition(disposition)

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      successSnackbar({ text: 'Arquivo CSV baixado com sucesso' });
    } catch (e) {
      console.error(e);
      errorSnackbar({ text: 'Erro ao baixar o CSV' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <ContentContainer>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: 18, fontWeight: 600 }}>
              {t('import.users.title')}
            </span>
            <Tooltip
              title={t('import.users.tooltip')}
              placement='right'
              arrow
            >
              <HelpOutline fontSize='18' />
            </Tooltip>
          </div>
          <Button
            variant='outlined'
            onClick={handleButtonClick}
          >
            {t('import.users.action')}
          </Button>
        </Header>
        <BodyContainer>
          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby='notificationsTable'
            >
              <TableHead
                headCells={headState}
              />
              <TableBody>
                {!loadingData ? (
                  <>
                    {rows.length > 0 ? (
                      <>
                        {visibleRows.map(row => {
                          return (
                            <TableRow
                              hover
                              tabIndex={-1}
                              key={row.id}
                            >
                              <CustomTableCell
                                data={row.date}
                                type='date'
                                sx={{ cursor: 'pointer' }}
                              />
                              <CustomTableCell data={row?.status} />
                              <CustomTableCell data={row?.createdBy} />
                              <CustomTableCell data={row?.importCreatedCount} />
                              <CustomTableCell data={row?.importUpdatedCount} />
                              <CustomTableCell data={row?.importErrorCount} />
                              <Button
                                variant="outlined"
                                onClick={() => handleDownloadCsv(row?.id)}
                              >
                                {t('import.users.table_action')}
                              </Button>
                            </TableRow>
                          )
                        })}
                        {emptyRows() > 0 && (
                          <TableRow
                            style={{
                              height: 53 * emptyRows(),
                            }}
                          >
                            <TableCell colSpan={5} />
                          </TableRow>
                        )}
                      </>
                    ) : (
                      <TableRow style={{ height: 53 * 10 }}>
                        <TableCell
                          colSpan={5}
                          align='center'
                        >
                          {t('no_data')}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ) : (
                  <TableRow style={{ height: 53 * 10 }}>
                    <TableCell
                      colSpan={5}
                      align='center'
                    >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component='div'
            count={rows.length}
            showFirstButton
            showLastButton
            labelRowsPerPage={''}
            rowsPerPageOptions={[]}
            rowsPerPage={10}
            page={page}
            onPageChange={handleChangePage}
            labelDisplayedRows={({ to, count }) =>
              {t('import.footer', {to: to, count: count})}
            }
          />
        </BodyContainer>
      </ContentContainer>
      <ImportActionsModal type='user' />
    </Container>
  )
}

const UserImport = () => {
  return (
    <ImportActionsModalProvider>
      <UserImportPage />
    </ImportActionsModalProvider>
  )
}

export default UserImport
